import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { Node } from "reactflow";

import { FlowChartConfigI, View } from "../../../types/PageInterfaces";
import AppSpinner from "../../../elements/AppSpinner";
import { RecordNode } from "./AppFlowChart";
import TenantAPI from "../../../api/api/TenantAPI";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useRecord from "../../../api/api-hooks/records/useRecord";
import SharedScreenAPI from "../../../api/api/SharedScreenAPI";

const AppFlowChart = lazy(() => import("./AppFlowChart"));

interface Props {
  view: View;
  providedFlowID?: string;
  chartName: string;
  loading: boolean;
}

export default function PageViewFlowChart({
  view,
  providedFlowID,
  chartName,
  loading,
}: Props) {
  const [params] = useSearchParams();
  const {
    sharedViewID,
    sharedPageID,
    sharedRecordPageID,
    tenantID: tID,
  } = useParams();
  const tenantID = useAtomValue(tenantIDAtom) ?? tID;
  const queryClient = useQueryClient();
  const [recordsLoading, setRecordsLoading] = useState(true);
  const isFirstRender = useRef(true);
  const flowID = providedFlowID || params.get("flow");
  const { record: flowRecord, recordLoading } = useRecord({
    moduleAPIName: "Flows",
    recordID: flowID || "",
  });

  useEffect(() => {
    if (
      !isFirstRender.current ||
      !tenantID ||
      recordLoading ||
      loading ||
      !flowRecord?.id
    )
      return;

    const onSuccess = () => {
      queryClient.setQueryData(["flowChartInitialized", flowRecord?.id], true);
      setRecordsLoading(false);
      isFirstRender.current = false;
    };

    if (queryClient.getQueryData(["flowChartInitialized", flowRecord?.id])) {
      return onSuccess();
    }

    initializeFlowChartRecords(
      flowRecord?.FlowChartNodes,
      flowRecord?.FlowChartConfig,
      tenantID,
      queryClient,
      { sharedViewID, sharedPageID, sharedRecordPageID },
      onSuccess
    );
  }, [
    flowRecord?.id,
    flowRecord?.FlowChartNodes,
    flowRecord?.FlowChartConfig,
    tenantID,
  ]);

  if (recordLoading || recordsLoading) {
    return (
      <div className="p-4 border rounded-sm bg-ultralight mt-4">
        <div className="d-flex align-items-center gap-2 mb-2">
          <h4 className="mb-0">Hang Tight...</h4>
          <AppSpinner color="primary" />
        </div>
        <p className="text-muted mb-0">We're fetching your Flow Chart.</p>
      </div>
    );
  }

  return (
    <Suspense>
      <AppFlowChart
        view={view}
        chartName={view?.Name || chartName}
        flowRecord={flowRecord}
        useFullScreen={!!(sharedViewID && !sharedPageID)}
      />
    </Suspense>
  );
}

// Instead of fetching each record individually in the flow chart,
// We perform a search query for records per module. This reduces
// the overall network requests greatly.
async function initializeFlowChartRecords(
  nodes: Node<RecordNode>[] | undefined,
  flowChartConfig: FlowChartConfigI | undefined,
  tenantID: string,
  queryClient: QueryClient,
  otherIds: {
    sharedViewID?: string;
    sharedPageID?: string;
    sharedRecordPageID?: string;
  },
  onSuccess: () => void
) {
  if (!nodes || !nodes.length || !flowChartConfig) return onSuccess();

  const { sharedViewID, sharedPageID, sharedRecordPageID } = otherIds;

  const recordsPerModule: Record<string, string[]> = {};

  const SearchPromise: any = sharedViewID
    ? SharedScreenAPI.searchRecordWithSharedView
    : sharedPageID
    ? SharedScreenAPI.searchRecordWithSharedPage
    : sharedRecordPageID
    ? SharedScreenAPI.searchRecordWithSharedRecordPage
    : TenantAPI.searchRecords;

  for (const node of nodes || []) {
    const recordID = node.data?.recordID;
    const moduleAPIName = node.data?.moduleAPIName;

    if (!moduleAPIName || !recordID) continue;

    if (!recordsPerModule[moduleAPIName]) {
      recordsPerModule[moduleAPIName] = [recordID];
      continue;
    }

    if (recordsPerModule[moduleAPIName].includes(recordID)) continue;

    recordsPerModule[moduleAPIName].push(recordID);
  }

  const modules = Object.keys(recordsPerModule);

  if (!modules.length) return onSuccess();

  const recordsPromises = modules.map((mod) => {
    return SearchPromise({
      moduleAPIName: mod,
      tenantID: tenantID,
      query: getByIdsQuery(
        flowChartConfig.Modules?.[mod]?.Fields || [],
        recordsPerModule[mod]
      ),
      sharedViewID: sharedViewID,
      sharedPageID: sharedPageID,
      sharedRecordPageID: sharedRecordPageID,
    });
  });

  try {
    const result = await Promise.all(recordsPromises);

    for (const res of result || []) {
      const records = res.data.data.data;

      for (const record of records || []) {
        queryClient.setQueryData(
          [
            "record",
            record._ModuleAPIName,
            record._id,
            flowChartConfig.Modules?.[record?._ModuleAPIName]?.Fields,
          ],
          record
        );
      }
    }
  } catch {
  } finally {
    onSuccess();
  }
}

function getByIdsQuery(fields: string[], ids: string[]) {
  return {
    Fields: fields,
    Filters: [{ Group: false, Field: "_id", Method: "in", Value: ids }],
  };
}
